import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, seededRandom } from "../spec";

const SHEET = "AV Consolidado";
const SUCURSALES = ["Centro", "Norte", "Sur", "Poniente", "Oriente", "Aeropuerto"] as const;
const MESES = ["Enero", "Febrero", "Marzo"] as const;

const rnd = seededRandom(4747);
// Same branches in the same order every month; every amount distinct.
const DATA: Record<(typeof MESES)[number], [string, number][]> = {
  Enero: [],
  Febrero: [],
  Marzo: [],
};
MESES.forEach((mes, m) =>
  SUCURSALES.forEach((s, i) => DATA[mes].push([s, 200000 + rnd.int(0, 300) * 1000 + m * 170 + i * 13]))
);

const todos = MESES.flatMap((mes) => DATA[mes]);
const EXPECTED: CellValue[][][] = [
  todos,
  [["Sucursal", "Ventas"], ...todos],
  SUCURSALES.map((s, i) => [s, DATA.Enero[i][1], DATA.Febrero[i][1], DATA.Marzo[i][1]]),
  SUCURSALES.map((s, i) => [s, DATA.Enero[i][1] + DATA.Febrero[i][1] + DATA.Marzo[i][1]]),
  [...todos].sort((a, b) => b[1] - a[1]),
  MESES.flatMap((mes) => DATA[mes].map((r) => [...r, mes])),
];
const FNS = [["VSTACK"], ["VSTACK"], ["HSTACK"], ["HSTACK"], ["SORT", "VSTACK"], ["VSTACK", "HSTACK", "EXPAND"]];
const LABELS = [
  "Tarea 1 · Los tres meses en una lista",
  "Tarea 2 · Con encabezados",
  "Tarea 3 · Meses lado a lado",
  "Tarea 4 · Total del trimestre",
  "Tarea 5 · Consolidado ordenado",
  "Tarea 6 · Consolidado con columna de mes",
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`K${row}`);
  return row + result.length + 3;
}, 6);

const EXPAND_FORMULA =
  '=APILARV(APILARH(Enero,EXPANDIR("Enero",FILAS(Enero),,"Enero")),APILARH(Febrero,EXPANDIR("Febrero",FILAS(Febrero),,"Febrero")),APILARH(Marzo,EXPANDIR("Marzo",FILAS(Marzo),,"Marzo")))';

export const spec: FuncionProjectSpec = {
  projectId: "funciones-apilar",
  sheets: [
    {
      name: SHEET,
      title: "Consolidar meses",
      subtitle: "Una tabla por mes — júntalas en un solo reporte sin copiar y pegar",
      tables: MESES.map((mes, m) => ({
        name: mes,
        anchor: `${["A", "D", "G"][m]}4`,
        headers: ["Sucursal", "Ventas"],
        rows: DATA[mes],
        formats: { Ventas: "$#,##0" },
      })),
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      {
        kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected,
        minCounts: i === 5 ? { HSTACK: 3 } : undefined,
      },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-apilar",
  title: "APILARV y APILARH: Consolidar meses",
  subtitle: "Juntar tablas hacia abajo o hacia los lados",
  description:
    "Cada mes llega una tabla con las ventas de 6 sucursales: Enero, Febrero y Marzo. Antes se copiaban y pegaban una debajo de otra; con APILARV se juntan con una fórmula que se actualiza sola. APILARH las pone lado a lado para comparar.\n\nAl final agregarás una columna con el nombre del mes usando EXPANDIR, la técnica que se usa para consolidar hojas en Excel moderno.",
  topics: [
    "APILARV: una tabla debajo de otra",
    "Agregar los encabezados una sola vez",
    "APILARH: columnas lado a lado",
    "Sumar meses directamente",
    "Ordenar un consolidado",
    "Etiquetar cada fila con EXPANDIR",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Los tres meses en una lista",
      description: `En la hoja <strong>AV Consolidado</strong>, celda <strong>${ANCHORS[0]}</strong>: junta las tablas <strong>Enero</strong>, <strong>Febrero</strong> y <strong>Marzo</strong> una debajo de otra.`,
      solution: [`Selecciona ${F(ANCHORS[0])}.`, `Escribe ${F("=APILARV(Enero,Febrero,Marzo)")}.`],
      expectedResult: "18 filas × 2 columnas",
      finalFormula: "=APILARV(Enero,Febrero,Marzo)",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Con encabezados",
      description: `En <strong>${ANCHORS[1]}</strong>: lo mismo, pero con la fila de encabezados (Sucursal, Ventas) <strong>una sola vez</strong> arriba.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        `${F("Enero[#Encabezados]")} es la fila de encabezados de la tabla Enero.`,
        `Escribe ${F("=APILARV(Enero[#Encabezados],Enero,Febrero,Marzo)")}.`,
      ],
      expectedResult: "19 filas: encabezado + 18",
      finalFormula: "=APILARV(Enero[#Encabezados],Enero,Febrero,Marzo)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Meses lado a lado",
      description: `En <strong>${ANCHORS[2]}</strong>: una tabla con la <strong>Sucursal</strong> y las ventas de <strong>Enero</strong>, <strong>Febrero</strong> y <strong>Marzo</strong> en columnas separadas.`,
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        "Las tres tablas tienen las sucursales en el mismo orden, así que basta con ponerlas lado a lado.",
        `Escribe ${F("=APILARH(Enero[Sucursal],Enero[Ventas],Febrero[Ventas],Marzo[Ventas])")}.`,
      ],
      expectedResult: "6 filas × 4 columnas",
      finalFormula: "=APILARH(Enero[Sucursal],Enero[Ventas],Febrero[Ventas],Marzo[Ventas])",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Total del trimestre",
      description: `En <strong>${ANCHORS[3]}</strong>: cada <strong>Sucursal</strong> con la <strong>suma</strong> de sus ventas de los tres meses.`,
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        "Sumar tres columnas del mismo tamaño suma fila por fila.",
        `Escribe ${F("=APILARH(Enero[Sucursal],Enero[Ventas]+Febrero[Ventas]+Marzo[Ventas])")}.`,
      ],
      expectedResult: "6 filas × 2 columnas",
      finalFormula: "=APILARH(Enero[Sucursal],Enero[Ventas]+Febrero[Ventas]+Marzo[Ventas])",
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Consolidado ordenado",
      description: `En <strong>${ANCHORS[4]}</strong>: las 18 filas de los tres meses ordenadas por Ventas de <strong>mayor a menor</strong>.`,
      solution: [`Selecciona ${F(ANCHORS[4])}.`, `Escribe ${F("=ORDENAR(APILARV(Enero,Febrero,Marzo),2,-1)")}.`],
      expectedResult: "18 filas de la venta más alta a la más baja",
      finalFormula: "=ORDENAR(APILARV(Enero,Febrero,Marzo),2,-1)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Consolidado con columna de mes",
      description: conditions(
        `En <strong>${ANCHORS[5]}</strong>: el consolidado de 18 filas con una <strong>tercera columna</strong> que diga de qué mes es cada fila:`,
        [
          "<strong>EXPANDIR(\"Enero\",FILAS(Enero),,\"Enero\")</strong> crea una columna con \"Enero\" repetido tantas veces como filas tiene la tabla",
          "<strong>APILARH</strong> pega esa columna a la derecha de cada tabla",
          "<strong>APILARV</strong> junta los tres meses",
        ]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        "EXPANDIR(matriz, filas, columnas, relleno) agranda un valor hasta el tamaño pedido y llena con el relleno.",
        `Escribe ${F(EXPAND_FORMULA)}.`,
      ],
      expectedResult: "18 filas × 3 columnas: Sucursal | Ventas | Mes",
      finalFormula: EXPAND_FORMULA,
    },
  ],
};
